import { useRef, useState } from 'react';
import { useAsync, useUnmount } from 'react-use';
import { loadImage } from 'utils/loadMedia/loadImage';
import { recolorMask } from 'utils/maskRecoloring/maskRecoloring';
import { UiRegionItem } from 'utils/cfUiItems/base';

const MaskCache = new Map<string, HTMLImageElement>();

async function getCachedOrCompute(key: string, compute: () => Promise<HTMLImageElement>): Promise<HTMLImageElement> {
  if (MaskCache.has(key)) {
    return MaskCache.get(key) as unknown as HTMLImageElement;
  }
  const val = await compute();
  MaskCache.set(key, val);
  return val;
}

async function recolorRegionMask(ref: React.MutableRefObject<string>, region: CF.API.MaskShapeProps, color: string): Promise<HTMLImageElement> {
  const { url, base64 } = region.mask.image;

  let colormask = '';
  if (url) {
    colormask = await recolorMask(url, color);
  }
  if (base64) {
    colormask = await recolorMask(`data:image/png;base64,${base64}`, color);
  }

  ref.current = colormask;
  return loadImage(colormask);
}

export const useLoadMask = (region: UiRegionItem, color: string): HTMLImageElement | null => {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const recoloredImgUrl = useRef<string>('');

  // load the image
  useAsync(async () => {
    if (region && 'mask' in region.regionData) {
      const coloredImg = await getCachedOrCompute(region.key, async () =>
        recolorRegionMask(recoloredImgUrl, region.regionData as CF.API.MaskShapeProps, color),
      );
      setImg(coloredImg);
    } else {
      setImg(null);
    }
  }, [region]);

  // object url was created, need to revoke it otherwise it'll exist forever
  useUnmount(() => {
    if (recoloredImgUrl.current) {
      URL.revokeObjectURL(recoloredImgUrl.current);
    }
  });

  return img;
};
